/**
 * Page INPUT-contract editor (ADR-0045 R4-inputs) — where the page AUTHOR
 * declares the `$input` parameters the page expects from its host (a `page_ref`
 * embed or an `open_overlay` opener). Lives in the Data dock's "Stato" tab,
 * next to the `$page` variables: both declare the page's data contract.
 *
 * Each row is a parameter: name (identifier) + optional type + required toggle +
 * description (shown as the guided-form hint on the host side). A declared input
 * also types `$input.<name>` for THIS page's bindings intellisense.
 *
 * The "Rileva input usati" button bootstraps the declaration from the page's own
 * `$input.<key>` usages (the `discovered` prop) — so the author rarely types a
 * name by hand: build the detail page reading `{{ $input.id }}`, click detect,
 * done. Auto-discovery ALSO works with zero declaration (the host falls back to
 * it), so declaring is the robustness upgrade, never a prerequisite.
 */
import type { PageInput, StateValueType } from "@/lib/pageTypes";

const INPUT =
  "w-full rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-ring";
const TYPES: StateValueType[] = ["string", "number", "boolean", "object", "array", "any"];
const NAME_RE = /^[A-Za-z_$][\w$]*$/;

export function PageInputsEditor({
  inputs,
  discovered,
  onChange,
}: {
  inputs: PageInput[];
  /** `$input.<key>` keys this page actually reads — power "Rileva input usati". */
  discovered: string[];
  onChange: (inputs: PageInput[]) => void;
}) {
  const names = new Set(inputs.map((i) => i.name));
  const missingDiscovered = discovered.filter((k) => !names.has(k));

  const add = (name = "") => {
    let n = name || "param";
    let i = 1;
    while (names.has(n)) n = `${name || "param"}${++i}`;
    onChange([...inputs, { name: n }]);
  };
  const patch = (idx: number, next: Partial<PageInput>) =>
    onChange(inputs.map((p, i) => (i === idx ? { ...p, ...next } : p)));
  const remove = (idx: number) => onChange(inputs.filter((_, i) => i !== idx));
  const detectAll = () => onChange([...inputs, ...missingDiscovered.map((name) => ({ name }))]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Ingressi ($input)
          </h3>
          <p className="text-[11px] leading-snug text-muted-foreground/80">
            I parametri che questa pagina si aspetta da chi la apre (page_ref / overlay).
          </p>
        </div>
        {missingDiscovered.length > 0 && (
          <button
            type="button"
            className="shrink-0 rounded border border-border px-2 py-1 text-[11px] text-accent hover:bg-surface"
            title={`Aggiunge gli input usati ma non dichiarati: ${missingDiscovered.join(", ")}`}
            onClick={detectAll}
          >
            Rileva input usati ({missingDiscovered.length})
          </button>
        )}
      </div>

      {inputs.length === 0 && (
        <p className="rounded border border-dashed border-border/60 p-2 text-[11px] text-muted-foreground/70">
          Nessun ingresso dichiarato. Chi apre questa pagina vedrà comunque i parametri
          rilevati dall'uso di {"{{ $input.* }}"}; dichiararli aggiunge tipo, obbligatorietà
          e descrizione.
        </p>
      )}

      {inputs.map((p, idx) => {
        const badName = Boolean(p.name) && !NAME_RE.test(p.name);
        return (
          <div key={idx} className="flex flex-col gap-1.5 rounded-md border border-border/60 bg-surface/40 p-2">
            {/* Row 1: name (full width) + delete. */}
            <div className="flex items-center gap-1">
              <input
                className={`${INPUT} flex-1 font-mono ${badName ? "border-danger" : ""}`}
                value={p.name}
                placeholder="nome (es. id)"
                aria-label="Nome ingresso"
                onChange={(e) => patch(idx, { name: e.target.value })}
              />
              <button
                type="button"
                className="shrink-0 px-1 text-muted-foreground hover:text-danger"
                title="Rimuovi ingresso"
                aria-label="Rimuovi ingresso"
                onClick={() => remove(idx)}
              >
                ×
              </button>
            </div>
            {/* Row 2: type + required. */}
            <div className="flex items-center gap-2">
              <select
                className={`${INPUT} flex-1`}
                value={p.type ?? ""}
                aria-label="Tipo ingresso"
                onChange={(e) => patch(idx, { type: (e.target.value || undefined) as StateValueType })}
              >
                <option value="">tipo: qualsiasi</option>
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <label className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={Boolean(p.required)}
                  aria-label="Obbligatorio"
                  onChange={(e) => patch(idx, { required: e.target.checked })}
                />
                Obbligatorio
              </label>
            </div>
            {/* Row 3: description (full width). */}
            <input
              className={INPUT}
              value={p.description ?? ""}
              placeholder="descrizione (mostrata a chi passa il parametro)"
              aria-label="Descrizione ingresso"
              onChange={(e) => patch(idx, { description: e.target.value || undefined })}
            />
            {badName && (
              <p className="text-[10px] text-danger">
                Nome non valido: usa lettere, numeri e underscore (no spazi).
              </p>
            )}
          </div>
        );
      })}

      <button
        type="button"
        className="self-start rounded border border-border px-1.5 py-0.5 text-[10px] text-accent hover:bg-surface"
        onClick={() => add()}
      >
        + Aggiungi ingresso
      </button>
    </div>
  );
}
